import { create } from 'zustand';
import { supabase } from './supabase';
import type { UserProfile, UserRole } from '@/types';

// DEV_MODE is ONLY allowed when (a) no Supabase URL is configured AND
// (b) the build is a dev build. The old `!VITE_SUPABASE_URL` test
// alone meant any prod deploy with a transient env-resolution glitch
// could silently fall into the DEV_USER admin path. (Finding 33 of
// the security audit.) Now `import.meta.env.DEV` gates the fallback
// hard — production code never enters the dev branch.
const DEV_MODE = !import.meta.env.VITE_SUPABASE_URL && import.meta.env.DEV;

// DEV_USER is ONLY used when DEV_MODE === true (gated above on
// import.meta.env.DEV + missing VITE_SUPABASE_URL). The agency_id
// here is intentionally a sentinel string — it does NOT correspond
// to any row in the production agencies table, so the dev session
// can't accidentally hit production RLS-scoped data. Finding 80 was
// originally about a mismatch between this sentinel and the
// synthesized-profile default; the synth path was removed in
// Sprint 1, so the only consumer left is the local dev shell.
const DEV_USER: UserProfile = {
  id: 'dev-user-001',
  email: 'dev@emspcr.app',
  full_name: 'Dev User',
  badge_number: 'D-001',
  cert_level: 'Paramedic',
  cert_expiration: null,
  role: 'admin',
  agency_id: 'dev-agency-001',
  station_id: 'Station 1',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

interface AuthState {
  user: UserProfile | null;
  session: { access_token: string } | null;
  loading: boolean;
  error: string | null;
  devMode: boolean;

  initialize: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  /**
   * Self-service signup for the closed pilot. The DB-side trigger
   * (migration 028) gates on:
   *   - any .gov email domain, OR
   *   - email present in the signup_email_allowlist table.
   * On success, the migration-029 handle_new_user() trigger
   * provisions the user_profiles row server-side (role='provider',
   * agency_id from the agencies.email_domain map) — the client
   * never writes user_profiles. Then fires welcome-email (Resend).
   */
  signUp: (email: string, password: string, fullName?: string, rank?: string) => Promise<{ ok: boolean; error?: string; needsConfirmation?: boolean }>;
  signOut: () => Promise<void>;
  /** Send a password-reset email. Returns error message if any. */
  resetPassword: (email: string) => Promise<string | null>;
  /** Set a new password. Must be called from a recovery session. */
  updatePassword: (newPassword: string) => Promise<string | null>;
  hasRole: (role: UserRole | UserRole[]) => boolean;
}

/**
 * Pilot domain gate — mirrored client-side for clearer error messages.
 * The authoritative check is the DB trigger (migration 028); this is
 * just so the form surfaces the error before the network round-trip.
 * Migration 028 (2026-05-11) broadened the original domain gate to
 * any .gov address; this regex matches that.
 */
const PILOT_DOMAIN_REGEX = /\.gov$/i;

/**
 * Fire-and-forget call to the notify-signin edge function so the owner
 * receives an email when a pilot user signs in. Failures are swallowed
 * — the user's sign-in succeeds either way; we never block on the
 * notification.
 */
async function fireSignInNotification(profile: UserProfile | null, accessToken: string | undefined): Promise<void> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl || !profile || !accessToken) return;
  try {
    await fetch(`${supabaseUrl}/functions/v1/notify-signin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        email: profile.email,
        full_name: profile.full_name,
        role: profile.role,
        user_agent: navigator.userAgent,
      }),
    });
  } catch {
    // intentional swallow — observability is nice-to-have, not required
  }
}

/**
 * Welcome email after self-signup. Same fire-and-forget pattern.
 */
/**
 * ProfileNotProvisionedError — thrown when a Supabase session has no
 * matching user_profiles row. The caller (initialize / signIn)
 * surfaces this as a hard "Account not provisioned" message and signs
 * the user out, rather than synthesizing a fake profile.
 *
 * Audit-driven change (Findings 1, 33, 63): the previous
 * implementation synthesized a profile from sessionUser.user_metadata
 * AND silently upserted it. user_metadata is user-controllable, so
 * a hostile signup with `data: { role: 'admin', agency_id: '…' }`
 * could land an admin row in user_profiles. We now never read role
 * or agency_id from metadata — the DB trigger handle_new_user
 * (migration 029) is the only path that writes user_profiles.
 */
export class ProfileNotProvisionedError extends Error {
  email: string | null;
  constructor(email: string | null) {
    super(
      `Account not provisioned for ${email ?? '(unknown email)'}. ` +
        `The server-side provisioning trigger did not create a profile — ` +
        `contact an admin to be added to an agency.`,
    );
    this.name = 'ProfileNotProvisionedError';
    this.email = email;
  }
}

/**
 * Resolve a UserProfile from a Supabase session.
 *
 * Strict read of public.user_profiles — no synthesis, no client-side
 * upsert. Row creation is owned by the migration-029 handle_new_user
 * trigger on auth.users insert; if the row is missing here, something
 * went wrong server-side and we refuse to fabricate one. This closes
 * the privilege-escalation vector documented as Finding 1 of the
 * security audit.
 */
async function resolveProfile(sessionUser: {
  id: string;
  email?: string | null;
}): Promise<UserProfile> {
  const { data: existing, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', sessionUser.id)
    .maybeSingle();
  if (error) {
    // Bubble DB errors so the caller can present a clear failure
    // state. RLS rejections and network errors both land here and
    // both deserve user-visible recovery — silent swallow would
    // re-introduce the synth-fallback class of bug.
    throw new Error(`[auth] failed to load profile: ${error.message}`);
  }
  if (!existing) {
    throw new ProfileNotProvisionedError(sessionUser.email ?? null);
  }
  return existing as UserProfile;
}

async function fireWelcomeEmail(email: string, fullName: string | null | undefined): Promise<void> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anon = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!supabaseUrl) return;
  try {
    await fetch(`${supabaseUrl}/functions/v1/welcome-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // welcome-email runs pre-confirmation so we use the anon key
        // (the user has no session yet). The function itself doesn't
        // touch DB-protected resources — just sends mail via Resend.
        ...(anon ? { Authorization: `Bearer ${anon}` } : {}),
      },
      body: JSON.stringify({
        to: email,
        full_name: fullName ?? null,
      }),
    });
  } catch {
    // ignore — user can always re-trigger from the resend confirmation flow
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  session: null,
  loading: true,
  error: null,
  devMode: DEV_MODE,

  initialize: async () => {
    // Dev mode — auto-login with mock user. Gated on `import.meta.env.DEV`
    // (see DEV_MODE constant above); production deploys can NEVER enter
    // this branch even if VITE_SUPABASE_URL is briefly missing.
    if (DEV_MODE) {
      set({ user: DEV_USER, session: { access_token: 'dev-token' }, loading: false, devMode: true });
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        try {
          const profile = await resolveProfile(session.user);
          set({
            session: { access_token: session.access_token },
            user: profile,
            loading: false,
          });
        } catch (err) {
          // No profile row → surface as an error and sign the user
          // out so they don't sit in a half-authenticated state.
          const msg = err instanceof Error ? err.message : String(err);
          // eslint-disable-next-line no-console
          console.warn('[auth] initialize: profile resolution failed —', msg);
          await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
          set({ session: null, user: null, loading: false, error: msg });
          return;
        }
      } else {
        set({ loading: false });
      }

      supabase.auth.onAuthStateChange(async (event, session) => {
        if (event === 'SIGNED_IN' && session) {
          try {
            const profile = await resolveProfile(session.user);
            set({
              session: { access_token: session.access_token },
              user: profile,
            });
            // Fire owner-notification for any sign-in path that lands
            // here (password, magic link, OAuth, recovery). Filter is
            // applied server-side so this is safe for every path.
            void fireSignInNotification(profile, session.access_token);
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            // eslint-disable-next-line no-console
            console.warn('[auth] SIGNED_IN: profile resolution failed —', msg);
            await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
            set({ session: null, user: null, error: msg });
          }
        } else if (event === 'SIGNED_OUT') {
          set({ session: null, user: null });
        }
      });
    } catch (err) {
      // Real failure path (network down, Supabase unreachable). DO NOT
      // fall back to DEV_USER in production — that was Finding 33 of
      // the security audit (transient network blip → silent admin
      // impersonation). Surface the error and let the user retry.
      const msg = err instanceof Error ? err.message : 'Authentication unavailable';
      // eslint-disable-next-line no-console
      console.error('[auth] initialize failed:', err);
      set({ user: null, session: null, loading: false, error: msg });
    }
  },

  signIn: async (email, password) => {
    if (DEV_MODE) {
      set({ user: DEV_USER, session: { access_token: 'dev-token' }, loading: false });
      return;
    }
    set({ loading: true, error: null });
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      set({ loading: false, error: error.message });
      return;
    }
    // Resolve and SET the profile right here so the UI bounces into
    // the app without waiting on the onAuthStateChange listener (which
    // can race with App.tsx's user-null guard and bounce the user back
    // to the login page).
    if (data.session) {
      try {
        const profile = await resolveProfile(data.session.user);
        set({
          session: { access_token: data.session.access_token },
          user: profile,
          loading: false,
        });
        // Fire-and-forget owner notification (server filters owner's own
        // email so Cameron isn't notified about his own sessions).
        void fireSignInNotification(profile, data.session.access_token);
      } catch (err) {
        // No profile row → server-side handle_new_user trigger never
        // ran or failed. Sign the user back out and surface a clear
        // error so they can contact an admin instead of sitting in a
        // half-authenticated state.
        const msg = err instanceof Error ? err.message : 'Account not provisioned';
        await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
        set({ session: null, user: null, loading: false, error: msg });
      }
    } else {
      set({ loading: false });
    }
  },

  signUp: async (email, password, fullName, rank) => {
    if (DEV_MODE) {
      set({ user: DEV_USER, session: { access_token: 'dev-token' }, loading: false });
      return { ok: true };
    }

    // Client-side domain hint. Authoritative gate is the DB trigger
    // (migration 028 — any .gov address or a row in
    // signup_email_allowlist). This check is only here so the form
    // surfaces the most-common rejection without a network round-trip.
    if (!PILOT_DOMAIN_REGEX.test(email.trim())) {
      // Don't hard-block — fall through to the server. The server
      // accepts allowlisted emails outside the .gov domain, and
      // returns a clear error otherwise.
    }

    set({ loading: true, error: null });
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        // user_metadata: full_name + rank flow through to
        // auth.users.raw_user_meta_data and downstream into the
        // synthesized UserProfile (see resolveProfile). Rank is
        // captured at signup so the chief / officer / firefighter
        // line appears on every audit row without a follow-up form.
        data: {
          full_name: fullName ?? null,
          rank: rank ?? null,
        },
        emailRedirectTo: `${window.location.origin}/`,
      },
    });

    if (error) {
      // The DB trigger surfaces its rejection here. Translate the
      // raw 'insufficient_privilege' into a user-readable note.
      const msg = /allowlist|insufficient_privilege|42501|pilot_signup_blocked/i.test(error.message)
        ? 'EMSPCR.app pilot signup requires a .gov email address. Contact Cameron Moquin (cameron@emspcr.app) for an allowlist exception.'
        : error.message;
      set({ loading: false, error: msg });
      return { ok: false, error: msg };
    }

    set({ loading: false });

    // Send the welcome email regardless of confirmation flow — Supabase
    // will also send its own confirm-email if email confirmation is on,
    // but the welcome message is our content (project pitch + build-
    // logic link) which the standard auth template can't carry.
    void fireWelcomeEmail(email, fullName);

    return {
      ok: true,
      needsConfirmation: !data.session, // null session = email confirmation required
    };
  },

  signOut: async () => {
    // Clear local state FIRST so the UI bounces to /login immediately
    // regardless of network state. Then fire the Supabase revocation
    // in the background with scope:'local' — local-only avoids the
    // global token-revoke round-trip that can hang or 4xx when the
    // session is already stale, leaving the user stranded on the app
    // shell with their state half-cleared. If the background call
    // fails we just log; the user is already logged out client-side.
    set({ session: null, user: null });
    if (!DEV_MODE) {
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[auth] supabase signOut errored (local state already cleared):', err);
      }
    }
  },

  resetPassword: async (email) => {
    if (DEV_MODE) return 'Password reset not available in dev mode';
    const redirectTo = `${window.location.origin}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    return error?.message ?? null;
  },

  updatePassword: async (newPassword) => {
    if (DEV_MODE) return 'Password update not available in dev mode';
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    return error?.message ?? null;
  },

  hasRole: (role) => {
    const { user } = get();
    if (!user) return false;
    if (Array.isArray(role)) return role.includes(user.role);
    return user.role === role;
  },
}));
